import { useState } from 'react'
import { X } from 'lucide-react'
import type { WireArticleBlock, WireAudioArtifact, WireCaptionsArtifact } from '../../api/types'
import { CmtWrapper } from '../common/cmt'
import type { CmtResult } from '../common/cmt/types'

type AudioBlock = Extract<WireArticleBlock, { type: 'audio' }>

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
      {children}
    </p>
  )
}

function AudioPicker({
  artifacts,
  onSelect,
  onClose,
}: {
  artifacts: WireAudioArtifact[]
  onSelect: (a: WireAudioArtifact) => void
  onClose: () => void
}) {
  return (
    <div className="rounded border border-line bg-canvas">
      <div className="flex items-center justify-between border-b border-line px-2 py-1.5">
        <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
          Select audio artifact
        </p>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-0.5 text-fg-secondary hover:bg-fill hover:text-fg"
          aria-label="Close picker"
        >
          <X size={12} />
        </button>
      </div>
      {artifacts.length === 0 ? (
        <p className="px-2 py-3 text-[11px] text-fg-secondary">No audio artifacts uploaded yet.</p>
      ) : (
        <div className="space-y-1 p-2">
          {artifacts.map(a => (
            <button
              key={a.id}
              type="button"
              onClick={() => onSelect(a)}
              className="flex w-full items-center gap-2 rounded border border-line bg-fill px-2 py-1.5 text-left transition-colors hover:border-accent-primary-solid"
            >
              <p className="truncate text-[11px] font-semibold text-fg">{a.filename}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function TranscriptPicker({
  artifacts,
  onSelect,
  onClose,
}: {
  artifacts: WireCaptionsArtifact[]
  onSelect: (a: WireCaptionsArtifact | null) => void
  onClose: () => void
}) {
  return (
    <div className="rounded border border-line bg-canvas">
      <div className="flex items-center justify-between border-b border-line px-2 py-1.5">
        <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
          Select transcript artifact
        </p>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-0.5 text-fg-secondary hover:bg-fill hover:text-fg"
          aria-label="Close picker"
        >
          <X size={12} />
        </button>
      </div>
      <div className="p-2">
        <button
          type="button"
          onClick={() => onSelect(null)}
          className="mb-1.5 w-full rounded border border-line bg-fill px-2 py-1 text-[11px] text-fg-secondary transition-colors hover:bg-canvas"
        >
          None
        </button>
        {artifacts.length === 0 ? (
          <p className="px-1 py-1 text-[11px] text-fg-secondary">No transcript artifacts uploaded yet.</p>
        ) : (
          <div className="space-y-1">
            {artifacts.map(a => (
              <button
                key={a.id}
                type="button"
                onClick={() => onSelect(a)}
                className="flex w-full items-center gap-2 rounded border border-line bg-fill px-2 py-1.5 text-left transition-colors hover:border-accent-primary-solid"
              >
                <p className="truncate text-[11px] font-semibold text-fg">{a.filename}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface Props {
  block: AudioBlock
  onUpdate: (updated: AudioBlock) => void
  audioArtifacts: WireAudioArtifact[]
  captionsArtifacts: WireCaptionsArtifact[]
}

export function AudioBlockPanel({ block, onUpdate, audioArtifacts, captionsArtifacts }: Props) {
  const [audioPickerOpen, setAudioPickerOpen] = useState(false)
  const [transcriptPickerOpen, setTranscriptPickerOpen] = useState(false)
  const [captionCmt, setCaptionCmt] = useState<CmtResult>({
    classification: block.captionClassification ?? 'U',
    disseminationControls: block.captionDisseminationControls ?? [],
  })

  const autoplay = block.autoplay ?? false
  const linkedArtifact = audioArtifacts.find(a => a.id === block.artifactId)

  function patch(p: Partial<AudioBlock>) {
    onUpdate({ ...block, ...p })
  }

  function handleCaptionCmtChange(cmt: CmtResult) {
    setCaptionCmt(cmt)
    patch({
      captionClassification: cmt.classification,
      captionDisseminationControls: cmt.disseminationControls.length ? cmt.disseminationControls : undefined,
    })
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <FieldLabel>Audio</FieldLabel>
          <button
            type="button"
            onClick={() => { setAudioPickerOpen(o => !o); setTranscriptPickerOpen(false) }}
            className="rounded border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
          >
            {block.artifactId ? 'Change' : 'Select'}
          </button>
        </div>
        {audioPickerOpen && (
          <div className="mb-2">
            <AudioPicker
              artifacts={audioArtifacts}
              onSelect={a => { patch({ artifactId: a.id }); setAudioPickerOpen(false) }}
              onClose={() => setAudioPickerOpen(false)}
            />
          </div>
        )}
        {linkedArtifact ? (
          <div className="rounded border border-line bg-fill px-2 py-1.5">
            <p className="truncate text-[11px] font-semibold text-fg">{linkedArtifact.filename}</p>
          </div>
        ) : (
          <p className="rounded border border-dashed border-red-7 px-2 py-2 text-center text-[11px] italic text-red-11 dark:text-red-11">
            No audio linked — select an artifact above
          </p>
        )}
      </div>

      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <FieldLabel>Transcript</FieldLabel>
          <button
            type="button"
            onClick={() => { setTranscriptPickerOpen(o => !o); setAudioPickerOpen(false) }}
            className="rounded border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
          >
            {block.transcriptArtifactId ? 'Change' : 'Set'}
          </button>
        </div>
        {transcriptPickerOpen && (
          <div className="mb-2">
            <TranscriptPicker
              artifacts={captionsArtifacts}
              onSelect={a => { patch({ transcriptArtifactId: a?.id ?? undefined }); setTranscriptPickerOpen(false) }}
              onClose={() => setTranscriptPickerOpen(false)}
            />
          </div>
        )}
        {block.transcriptArtifactId && (
          <div className="rounded border border-line bg-fill px-2 py-1.5">
            <p className="truncate text-[11px] font-semibold text-fg">
              {captionsArtifacts.find(a => a.id === block.transcriptArtifactId)?.filename ?? block.transcriptArtifactId}
            </p>
          </div>
        )}
      </div>

      <div>
        <FieldLabel>Title</FieldLabel>
        <input
          type="text"
          value={block.title ?? ''}
          onChange={e => patch({ title: e.target.value || undefined })}
          placeholder="Optional title"
          className="w-full rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg placeholder:text-fg-secondary/60 focus:border-accent-primary-solid focus:outline-none"
        />
      </div>

      <div>
        <FieldLabel>Playback</FieldLabel>
        <label className="flex cursor-pointer items-center gap-2 text-[11px] text-fg">
          <input
            type="checkbox"
            checked={autoplay}
            onChange={e => patch({ autoplay: e.target.checked })}
            className="accent-accent-primary-solid"
          />
          Autoplay
        </label>
      </div>

      <div>
        <FieldLabel>Alt Text</FieldLabel>
        <input
          type="text"
          value={block.altText ?? ''}
          onChange={e => patch({ altText: e.target.value || undefined })}
          placeholder="Describe the audio for accessibility"
          className="w-full rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg placeholder:text-fg-secondary/60 focus:border-accent-primary-solid focus:outline-none"
        />
      </div>

      <div>
        <FieldLabel>Caption</FieldLabel>
        <textarea
          value={block.caption ?? ''}
          onChange={e => patch({ caption: e.target.value || undefined })}
          rows={2}
          placeholder="Caption text..."
          className="w-full resize-none rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg placeholder:text-fg-secondary/60 focus:border-accent-primary-solid focus:outline-none"
        />
        {block.caption && (
          <div className="mt-1.5">
            <FieldLabel>Caption Classification</FieldLabel>
            <CmtWrapper value={captionCmt} onChange={handleCaptionCmtChange} compact />
          </div>
        )}
      </div>

      <div>
        <FieldLabel>Credit</FieldLabel>
        <input
          type="text"
          value={block.credit ?? ''}
          onChange={e => patch({ credit: e.target.value || undefined })}
          placeholder="Source or attribution"
          className="w-full rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg placeholder:text-fg-secondary/60 focus:border-accent-primary-solid focus:outline-none"
        />
      </div>
    </div>
  )
}
